'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient, ApiClientError } from '@/lib/api-client';
import { createClient } from '@/lib/supabase/client';
import { setBetterStackUserPlan } from '@/lib/betterstack-tag';
import { useRealtimeLiveness } from '@/lib/realtime/use-realtime-liveness';
import { trackEvent } from '@/lib/analytics';
import { Logger } from '@/lib/logger';
import { isStaleScanObservation } from '@/lib/scan-observation';
import { useScanAuthentication, useScanStatusLifecycle } from './use-scan-status-lifecycle';
import {
  isActiveStatus,
  isTerminalStatus,
  shouldAutoStartFullScan,
  type ScanJobData,
  type ScanStatusController,
  type ScanStatusState,
} from './use-scan-status.types';

export function useScanStatusController(): ScanStatusController {
  const [state, setState] = useState<ScanStatusState>({
    currentJob: null,
    lastCompletedJob: null,
    gmailStatus: null,
    isLoading: true,
    error: null,
    progress: 0,
    tier: 'free',
    needsFullScan: false,
    emailsLimit: null,
  });

  const {
    isLive: isRealtimeLive,
    markSubscribed: markRealtimeSubscribed,
    markEvent: markRealtimeEvent,
    markDisconnected: markRealtimeDisconnected,
  } = useRealtimeLiveness('dashboard_scan_status');

  const realtimeChannelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const activeJobIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  // Break the fetchStatus/subscribeToJob dependency cycle.
  const fetchStatusRef = useRef<(() => Promise<ScanJobData | null | undefined>) | null>(null);

  const safeSetState = useCallback((updater: (prev: ScanStatusState) => ScanStatusState) => {
    if (mountedRef.current) setState((previous) => {
      const next = updater(previous);
      const observation = next.currentJob ?? next.lastCompletedJob;
      if (observation && isStaleScanObservation(previous.currentJob ?? previous.lastCompletedJob, observation)) {
        return { ...next, currentJob: previous.currentJob, lastCompletedJob: previous.lastCompletedJob, progress: previous.progress };
      }
      return next;
    });
  }, []);

  const calculateProgress = useCallback((job: ScanJobData | null, limit?: number | null): number => {
    if (!job) return 0;
    if (job.status === 'COMPLETED') return 100;
    if (job.status === 'FAILED' || job.status === 'CANCELED') return 0;
    if (job.counts_verified === false || job.listing_complete === false) return 0;
    const denominator = limit ?? job.emails_listed;
    if (denominator === 0) return 0;
    if (job.emails_fetched > denominator) return 0;
    return Math.min(99, Math.round((job.emails_fetched / denominator) * 100));
  }, []);

  const cleanupRealtimeSubscription = useCallback(() => {
    markRealtimeDisconnected();
    const channel = realtimeChannelRef.current;
    activeJobIdRef.current = null;
    if (!channel) return;
    realtimeChannelRef.current = null;

    const supabase = createClient();
    void supabase.removeChannel(channel).catch(() => {
      void channel.unsubscribe();
    });
  }, [markRealtimeDisconnected]);

  const fetchCurrentJob = useCallback(async () => {
    try {
      const currentJob = await apiClient.scan.current.query();
      if (!currentJob) return await fetchStatusRef.current?.() ?? null;

      safeSetState((prev) => ({
        ...prev,
        currentJob: currentJob ?? null,
        isLoading: false,
        error: null,
        progress: calculateProgress(currentJob ?? null, prev.emailsLimit),
      }));

      return currentJob ?? null;
    } catch (err) {
      Logger.warn('Unable to refresh current scan status', { error: err });
      const message = err instanceof ApiClientError ? err.userMessage : 'Failed to fetch scan status';
      safeSetState((prev) => ({ ...prev, isLoading: false, error: message }));
      return null;
    }
  }, [calculateProgress, safeSetState]);

  const subscribeToJob = useCallback((job_id: string) => {
    if (activeJobIdRef.current === job_id && realtimeChannelRef.current) {
      return;
    }

    cleanupRealtimeSubscription();
    activeJobIdRef.current = job_id;

    const supabase = createClient();
    const channel = supabase
      .channel(`scan_status:${job_id}:${Date.now()}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'scan_jobs',
        filter: `id=eq.${job_id}`,
      }, (payload) => {
        markRealtimeEvent();
        const updated = payload.new as ScanJobData;
        const isTerminal = isTerminalStatus(updated.status);

        // Merge raw rows to preserve server-computed fields until API refresh.
        safeSetState((prev) => ({
          ...prev,
          currentJob: isTerminal
            ? null
            : { ...(prev.currentJob ?? {}), ...updated } as ScanJobData,
          lastCompletedJob: isTerminal ? { ...prev.currentJob, ...updated } : prev.lastCompletedJob,
          progress: updated.status === 'COMPLETED' ? 100 : calculateProgress(updated, prev.emailsLimit),
        }));

        if (isTerminal) {
          cleanupRealtimeSubscription();
          // Refresh terminal computed fields immediately, not on the idle poll.
          void fetchStatusRef.current?.();
        } else {
          void fetchCurrentJob();
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          markRealtimeSubscribed();
          return;
        }

        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          markRealtimeDisconnected();
        }
      });

    realtimeChannelRef.current = channel;
  }, [calculateProgress, cleanupRealtimeSubscription, fetchCurrentJob, markRealtimeDisconnected, markRealtimeEvent, markRealtimeSubscribed, safeSetState]);

  const fetchStatus = useCallback(async () => {
    try {
      const [currentJob, gmailStatus, historyResult, subscription] = await Promise.all([
        apiClient.scan.current.query(),
        apiClient.auth.getGmailOauthStatus(),
        apiClient.scan.history.query({ limit: 10 }),
        apiClient.subscription.getStatus(),
      ]);

      const lastCompleted = historyResult?.jobs?.find((job: ScanJobData) => isTerminalStatus(job.status)) ?? null;
      const previousJobId = activeJobIdRef.current;
      if (!currentJob && previousJobId
        && !historyResult?.jobs?.some((job: ScanJobData) => job.id === previousJobId && isTerminalStatus(job.status))) {
        throw new Error('Current scan unavailable without a confirmed terminal result');
      }
      const tier = (subscription?.tier as 'free' | 'pro') ?? 'free';
      const emailsLimit = subscription?.emails_limit ?? null;

      setBetterStackUserPlan(tier);

      let jobToUse = currentJob ?? null;
      if (
        shouldAutoStartFullScan({
          tier,
          needsFullScan: subscription?.needs_full_scan ?? false,
          autoEligible: subscription?.auto_full_scan_eligibility?.eligible,
          lastCompletionQuality: lastCompleted?.completion_quality,
          jobStatus: jobToUse?.status,
          gmailStatus,
        })
      ) {
        try {
          const result = await apiClient.scan.start.mutate({ scan_mode: 'full', intent: 'automatic' });
          if (result?.job_id) {
            trackEvent('pro_full_scan_auto_started', {
              trigger: 'subscription_activation',
            });
            jobToUse = await apiClient.scan.current.query();
          }
        } catch (error) {
          Logger.warn('Automatic full scan start failed', { error });
        }
      }

      safeSetState((prev) => ({
        ...prev,
        currentJob: jobToUse,
        lastCompletedJob: lastCompleted,
        gmailStatus: gmailStatus ?? null,
        isLoading: false,
        error: null,
        tier,
        needsFullScan: subscription?.needs_full_scan === true,
        emailsLimit,
        progress: calculateProgress(jobToUse, emailsLimit),
      }));

      if (jobToUse?.id && isActiveStatus(jobToUse.status)) {
        subscribeToJob(jobToUse.id);
      } else {
        cleanupRealtimeSubscription();
      }

      return jobToUse;
    } catch (err) {
      Logger.warn('Unable to refresh scan status', { error: err });
      const message = err instanceof ApiClientError ? err.userMessage : 'Failed to fetch scan status';
      safeSetState((prev) => ({ ...prev, isLoading: false, error: message }));
      return undefined;
    }
  }, [calculateProgress, cleanupRealtimeSubscription, safeSetState, subscribeToJob]);

  useEffect(() => {
    fetchStatusRef.current = fetchStatus;
  }, [fetchStatus]);

  const isAuthenticated = useScanAuthentication(cleanupRealtimeSubscription, safeSetState);
  useScanStatusLifecycle({
    authenticated: isAuthenticated,
    activeStatus: state.currentJob?.status,
    realtimeLive: isRealtimeLive,
    mountedRef,
    fetchCurrentJob,
    fetchStatus,
    subscribeToJob,
    cleanupSubscription: cleanupRealtimeSubscription,
    setState: safeSetState,
  });

  const startScan = useCallback(async (mode?: 'full' | 'delta') => {
    try {
      const terminal = state.lastCompletedJob ?? state.currentJob;
      const retryFull = terminal?.scan_mode === 'full' && ['FAILED', 'CANCELED'].includes(terminal.status);
      const result = await apiClient.scan.start.mutate({ scan_mode: mode ?? (retryFull ? 'full' : 'delta'), intent: 'manual' });
      const job = await apiClient.scan.current.query();

      safeSetState((prev) => ({
        ...prev,
        currentJob: job ?? null,
        progress: 0,
        error: null,
      }));

      if (job?.id) {
        subscribeToJob(job.id);
      }

      return result;
    } catch (err) {
      if (
        err instanceof ApiClientError &&
        err.statusCode === 409 &&
        (err.code === 'CONFLICT' || err.code === 'SCAN_ALREADY_IN_PROGRESS')
      ) {
        Logger.info('Manual scan start conflicted with existing work', { code: err.code });
        return null;
      }
      throw err;
    }
  }, [safeSetState, subscribeToJob, state.currentJob, state.lastCompletedJob]);

  const pauseScan = useCallback(async () => {
    if (!state.currentJob) return;
    await apiClient.scan.pause.mutate({ job_id: state.currentJob.id });
    await fetchStatus();
  }, [fetchStatus, state.currentJob]);

  const resumeScan = useCallback(async () => {
    if (!state.currentJob) return null;

    const result = await apiClient.scan.resume.mutate({ job_id: state.currentJob.id });
    await fetchStatus();
    return result ?? null;
  }, [fetchStatus, state.currentJob]);

  const cancelScan = useCallback(async () => {
    if (!state.currentJob) return;

    await apiClient.scan.cancel.mutate({ job_id: state.currentJob.id });
    await fetchStatus();
  }, [fetchStatus, state.currentJob]);

  const refresh = useCallback(() => fetchStatus(), [fetchStatus]);
  return { ...state, startScan, pauseScan, resumeScan, cancelScan, refresh };
}
